// Blocking session-expired modal (#294). Renders over the current route
// without unmounting anything underneath, so React state, form input, scroll
// position, and the current route survive re-auth.
//
// Non-dismissible by design: no Esc handler, no outside-click dismiss (via
// ModalShell dismissable={false}), no ✕ button. The only exit is completing
// OTP (onSuccess) or the explicit "Sign out instead" button (onCancel).
// Focus is trapped via ModalShell → useFocusTrap, the house convention.
//
// The OTP email is locked to the expired identity: parked requests resume
// under the re-verified session, so they must never be replayed under a
// different user's token.
import { useEffect, useRef } from 'react';
import { ModalShell, ModalHeader } from '../ModalShell';
import { OtpForm } from './OtpForm';

interface SessionExpiredModalProps {
  open: boolean;
  email: string | null;
  onSuccess: (accessToken: string, email: string) => void;
  onCancel: () => void;
}

export function SessionExpiredModal({ open, email, onSuccess, onCancel }: SessionExpiredModalProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // Disabling the focused submit button can leave focus on body. Capture
    // those events before background shortcuts, while keeping dialog typing
    // and native browser commands available. Escape never closes lower layers.
    const consumeKeyboard = (e: KeyboardEvent) => {
      const inside = e.target instanceof Node && rootRef.current?.contains(e.target);
      if (inside && e.key !== 'Escape') return;
      const browserCommand = e.ctrlKey || e.metaKey || e.altKey;
      if (e.key === 'Escape' || !browserCommand) e.preventDefault();
      e.stopPropagation();
      if (!inside && e.type === 'keydown' && e.key === 'Tab' && !browserCommand) {
        const controls = Array.from(rootRef.current?.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled])',
        ) ?? []).filter(element => element.offsetParent !== null);
        (e.shiftKey ? controls[controls.length - 1] : controls[0])?.focus();
      }
    };
    window.addEventListener('keydown', consumeKeyboard, true);
    window.addEventListener('keyup', consumeKeyboard, true);
    return () => {
      window.removeEventListener('keydown', consumeKeyboard, true);
      window.removeEventListener('keyup', consumeKeyboard, true);
    };
  }, [open]);

  if (!open) return null;
  // onClose is required by ModalShell but unreachable while dismissable is
  // false (no ✕, no Esc, no backdrop dismiss) — the only exits are OTP
  // success and the explicit "Sign out instead" button below.
  return (
    <div ref={rootRef} onKeyDown={event => event.stopPropagation()} onKeyUp={event => event.stopPropagation()}>
      {/* Body portals escape background inertness and use layers up to 9999. */}
      <ModalShell zIndex={10000} onClose={() => undefined} dismissable={false} ariaLabel="Sign back in to keep going" maxWidth="max-w-md">
        <ModalHeader
          eyebrow="Session expired"
          title="Sign back in to keep going"
          subtitle="Your session expired. We'll email you a fresh code — request it below and we'll pick up right where you left off."
        />
        <OtpForm
          initialEmail={email ?? ''}
          onSuccess={onSuccess}
          onCancel={onCancel}
          cancelLabel="Sign out instead"
          emailLocked
        />
      </ModalShell>
    </div>
  );
}
